import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../../lib/utils';
import { secondaryText } from '../../../../../../styling/stylingHelper';
import {
  recheckFlaggedProduct,
  resolveAICheckerQuantityFlag,
  resolveAICheckerQuantityFlagsBulk,
  setAICheckerQuantityFlagKnown,
  sendIssueToCodeMonkey,
  type FlaggedProductWithRestaurant,
  type RecheckFlaggedProductResult,
} from '../../../../../../services/admin/adminService';
import { Check, RotateCw, Undo2 } from 'lucide-react';
import MonkeyIcon from '../../../../../common/MonkeyIcon';
import { buildGroupedQuantityCaseInfo } from '../../shared/quantityPrompt';
import { aiQtyFlagKey } from './aiQtyFlagKey';
import { isCleanRecheck, summarizeQtyRecheck, type QtyRecheckOutcome, type QtyRecheckSummary } from './qtyFlagGrouping';
import type { MonkeyState, TFn } from '../../types';

// Inline per-row recheck: re-runs the AI quantity audit on the flag's current data without opening
// the detail modal, and prints the outcome as a small pill in the same line (deep-check style).
// A clean recheck auto-marks the flag done — `onResolved` is only passed by the OPEN list, so the
// done/known lists keep the read-only "looks resolved" pill instead of flipping their own state.
// `duplicates` = every flag a de-duplicated row stands for: rechecked one at a time because each is an AI call.
const QTY_RECHECK_RESOLVED_DELAY_MS = 1200;
// "File verified" is news the operator should get to read before the row leaves the list.
const QTY_RECHECK_FILE_VERIFIED_DELAY_MS = 3000;

export function QtyFlagRecheckButton({ flag, duplicates, onResolved, t }: { flag: FlaggedProductWithRestaurant; duplicates?: FlaggedProductWithRestaurant[]; onResolved?: (keys: string[]) => void; t: TFn }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<RecheckFlaggedProductResult | null>(null);
  const [autoDone, setAutoDone] = useState(false);
  const [fileVerified, setFileVerified] = useState(false);
  const [summary, setSummary] = useState<QtyRecheckSummary | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  // A row filtered away mid-run stops spending AI calls; what it already closed still leaves the list.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const recheck = (f: FlaggedProductWithRestaurant) => recheckFlaggedProduct({
    setupSource: f.setupSource,
    platform: f.platform,
    productName: f.productName,
    articleId: f.articleId,
    restaurantName: f.restaurantName,
    gmailId: f.gmailId,
    source: f.source ?? 'pakbon',
    flaggedQuantityInfo: f.quantityInfo,
  });
  const resolve = (f: FlaggedProductWithRestaurant) => resolveAICheckerQuantityFlag(f.restaurantId, f.articleId, f.productName, f.source ?? 'pakbon', true, { fromRecheck: true });
  const errorText = (err: any) => (err?.message === 'supplier_busy' ? t('adminTesting.review.recheckSupplierBusy') : err?.message || t('adminTesting.flaggedDetail.recheckError'));

  const recheckAll = async (targets: FlaggedProductWithRestaurant[]) => {
    const outcomes: QtyRecheckOutcome[] = [];
    const closedKeys: string[] = [];
    for (const target of targets) {
      if (!mounted.current) break;
      try {
        const res = await recheck(target);
        if (isCleanRecheck(res) && onResolved) {
          const resolved = await resolve(target);
          closedKeys.push(aiQtyFlagKey(target));
          outcomes.push({ outcome: 'closed', fileVerified: resolved.fileVerified });
        } else {
          outcomes.push({ outcome: 'open' });
        }
      } catch (err) {
        console.error('Failed to recheck quantity flag', target.productName, err);
        outcomes.push({ outcome: 'failed', error: errorText(err) });
      }
    }
    const next = summarizeQtyRecheck(outcomes);
    setSummary(next);
    setState('done');
    if (closedKeys.length > 0 && onResolved) {
      setTimeout(() => onResolved(closedKeys), next.filesVerified > 0 ? QTY_RECHECK_FILE_VERIFIED_DELAY_MS : QTY_RECHECK_RESOLVED_DELAY_MS);
    }
  };

  const handleClick = async () => {
    if (state === 'loading') return;
    setState('loading');
    setSummary(null);
    // A multi-delivery factuur repeats one flag identity. The endpoints address them as one, so check it once.
    const targets = Array.from(new Map((duplicates ?? [flag]).map((d) => [aiQtyFlagKey(d), d])).values());
    if (targets.length > 1) {
      await recheckAll(targets);
      return;
    }
    try {
      const res = await recheck(flag);
      setResult(res);
      setState('done');
      // `unchanged` = the stored data is still exactly what was flagged. A clean AI verdict on it is
      // just a second opinion on the same judgement call, so it must not close the flag.
      if (isCleanRecheck(res) && onResolved) {
        const resolved = await resolve(flag);
        setAutoDone(true);
        setFileVerified(resolved.fileVerified);
        // Leave the green pill on screen for a beat before the row leaves the open list.
        setTimeout(() => onResolved([aiQtyFlagKey(flag)]), resolved.fileVerified ? QTY_RECHECK_FILE_VERIFIED_DELAY_MS : QTY_RECHECK_RESOLVED_DELAY_MS);
      }
    } catch (err) {
      console.error('Failed to recheck quantity flag', flag.productName, err);
      setErrorMsg(errorText(err));
      setState('error');
    }
  };

  const summaryPill = (s: QtyRecheckSummary) => {
    const closedText = s.closed === s.total ? t('adminTesting.review.recheckManyAllClosed', { total: s.total }) : t('adminTesting.review.recheckManySomeClosed', { closed: s.closed, total: s.total });
    const msg = [
      closedText,
      s.filesVerified > 0 ? t('adminTesting.review.recheckFileVerified') : '',
      s.failed > 0 ? t('adminTesting.review.recheckAllFailed', { count: s.failed }) : '',
      s.error ?? '',
    ].filter(Boolean).join(' · ');
    const cls = s.tone === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
      : s.tone === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
      : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300';
    return { cls, msg };
  };

  const pill = state === 'done' && summary
    ? summaryPill(summary)
    : state === 'done' && result
    ? result.inconclusive
      ? { cls: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300', msg: t('adminTesting.review.recheckInconclusive') }
      : result.unchanged && !result.stillFlagged
      ? { cls: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300', msg: t('adminTesting.review.recheckUnchanged') }
      : result.stillFlagged
      ? { cls: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300', msg: t('adminTesting.flaggedDetail.stillFlagged', { reason: result.flaggedProducts[0]?.reason ?? flag.reason }) }
      : { cls: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300', msg: autoDone ? [t('adminTesting.review.recheckAutoDone'), fileVerified ? t('adminTesting.review.recheckFileVerified') : ''].filter(Boolean).join(' · ') : t('adminTesting.flaggedDetail.resolved') }
    : state === 'error'
      ? { cls: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300', msg: errorMsg }
      : null;

  return (
    <span className="inline-flex items-center gap-1.5">
      {pill && (
        <span className={cn('text-[10px] px-1.5 py-0.5 rounded max-w-[220px] truncate', pill.cls)} title={pill.msg}>
          {pill.msg}
        </span>
      )}
      <button
        onClick={handleClick}
        disabled={state === 'loading'}
        title={t('adminTesting.flaggedDetail.recheck')}
        className={cn('p-1 rounded-md transition-colors disabled:opacity-50 text-gray-500 dark:text-gray-400',
          'hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-400')}
      >
        <RotateCw className={cn('w-4 h-4', state === 'loading' && 'animate-spin text-indigo-600 dark:text-indigo-400')} />
      </button>
    </span>
  );
}

// Persistent per-flag toggle: "mark done" (resolved=true) or "undo" (resolved=false), flipping
// `resolved` in the email's quantity_flags JSONB. `duplicates` = every flag a de-duplicated row stands for.
export function QtyFlagDoneButton({ flag, duplicates, resolved, onDone }: { flag: FlaggedProductWithRestaurant; duplicates?: FlaggedProductWithRestaurant[]; resolved: boolean; onDone: () => void }) {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT('en');
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (duplicates && duplicates.length > 1) {
        await resolveAICheckerQuantityFlagsBulk(duplicates.map((d) => ({
          traceId: d.restaurantId,
          articleId: d.articleId,
          productName: d.productName,
          source: d.source ?? 'pakbon',
        })), !resolved);
      } else {
        await resolveAICheckerQuantityFlag(flag.restaurantId, flag.articleId, flag.productName, flag.source ?? 'pakbon', !resolved);
      }
      onDone();
    } catch (err) {
      console.error('Failed to update quantity flag', err);
      setBusy(false);
    }
  };
  // resolved=true means it's currently done → the action is to reopen (undo).
  const label = resolved ? t('adminTesting.review.markOpen') : t('adminTesting.review.markDone');
  return (
    <button
      onClick={handle}
      disabled={busy}
      title={label}
      className={cn('p-1 rounded-md transition-colors disabled:opacity-50 text-gray-500 dark:text-gray-400',
        resolved
          ? 'hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:text-amber-600 dark:hover:text-amber-400'
          : 'hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-600 dark:hover:text-green-400')}
    >
      {resolved
        ? <Undo2 className={cn('w-4 h-4', busy && 'animate-pulse')} />
        : <Check className={cn('w-4 h-4', busy && 'animate-pulse')} />}
    </button>
  );
}

/** Undo a quantity flag that was filed as a known to-do — clears `known` so it returns to the open list. */
export function QtyFlagUndoKnownButton({ flag, onUndone }: { flag: FlaggedProductWithRestaurant; onUndone: () => void }) {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT('en');
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await setAICheckerQuantityFlagKnown(flag.restaurantId, flag.articleId, flag.productName, '', flag.source ?? 'pakbon', false);
      onUndone();
    } catch (err) {
      console.error('Failed to undo known quantity flag', err);
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={busy}
      title={t('adminTesting.review.undoKnown')}
      className={cn('p-1 rounded-md transition-colors disabled:opacity-50 text-gray-500 dark:text-gray-400',
        'hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:text-amber-600 dark:hover:text-amber-400')}
    >
      <Undo2 className={cn('w-4 h-4', busy && 'animate-pulse')} />
    </button>
  );
}

// Small colored pill showing which table a flag came from (pakbon vs factuur). Missing source (rows
// written before the factuur-audit spec) defaults to 'pakbon'.
export function SourceBadge({ source, t }: { source?: 'pakbon' | 'factuur'; t: TFn }) {
  const isFactuur = (source ?? 'pakbon') === 'factuur';
  return (
    <span
      className={cn(
        'shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap',
        isFactuur
          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
      )}
    >
      {isFactuur ? t('adminTesting.review.sourceFactuur') : t('adminTesting.review.sourcePakbon')}
    </span>
  );
}

// Per-supplier bulk action: mark every open flag in one platform group done with a single call
// (confirm first — this can flip dozens of flags; an accidental click is undoable via "show done").
export function QtyFlagMarkAllDoneButton({ platform, products, onDone, t }: { platform: string; products: FlaggedProductWithRestaurant[]; onDone: () => void; t: TFn }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (busy) return;
    if (!window.confirm(t('adminTesting.review.markAllDoneConfirm', { count: products.length, platform }))) return;
    setBusy(true);
    try {
      await resolveAICheckerQuantityFlagsBulk(products.map((p) => ({
        traceId: p.restaurantId,
        articleId: p.articleId,
        productName: p.productName,
        source: p.source ?? 'pakbon',
      })));
      onDone();
    } catch (err) {
      console.error('Failed to bulk-mark quantity flags done', err);
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={busy}
      className={cn(secondaryText, 'text-[10px] font-sans font-normal underline hover:text-green-600 dark:hover:text-green-400 disabled:opacity-50', busy && 'animate-pulse')}
    >
      {t('adminTesting.review.markAllDone')}
    </button>
  );
}

// Per-supplier bulk recheck: re-runs the AI quantity audit on every open flag in the group, then marks
// the ones that come back clean done in ONE bulk call. Still-flagged ones are left untouched. It is one
// Gemini call per flag, so it confirms first and runs a handful at a time instead of all at once.
const QTY_RECHECK_ALL_CONCURRENCY = 4;

export function QtyFlagRecheckAllButton({ platform, products, onResolved, t }: { platform: string; products: FlaggedProductWithRestaurant[]; onResolved: (keys: string[]) => void; t: TFn }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<{ resolved: number; stillFlagged: number; failed: number } | null>(null);

  const handle = async () => {
    if (busy) return;
    if (!window.confirm(t('adminTesting.review.recheckAllConfirm', { count: products.length, platform }))) return;
    setBusy(true);
    setProgress(0);
    setSummary(null);
    // The same product flagged on several emails re-audits to the same answer (the recheck reads the
    // product's own recent lines, not the email) — check each (source, article, name) once and apply
    // the verdict to every flag that shares it, instead of paying for one AI call per row.
    const byProduct = new Map<string, FlaggedProductWithRestaurant[]>();
    for (const p of products) {
      const key = `${p.source ?? 'pakbon'}|${p.articleId}|${p.productName}|${p.restaurantName}|${p.setupSource?.restaurantId ?? ''}|${p.setupSource?.fileId ?? ''}`;
      if (!byProduct.has(key)) byProduct.set(key, []);
      byProduct.get(key)!.push(p);
    }
    const queue = Array.from(byProduct.values());
    setTotal(queue.length);
    const resolved: FlaggedProductWithRestaurant[] = [];
    let stillFlagged = 0;
    let failed = 0;
    const worker = async () => {
      while (queue.length > 0) {
        const group = queue.shift()!;
        const p = group[0];
        try {
          const res = await recheckFlaggedProduct({
            setupSource: p.setupSource,
            platform: p.platform,
            productName: p.productName,
            articleId: p.articleId,
            restaurantName: p.restaurantName,
            gmailId: p.gmailId,
            source: p.source ?? 'pakbon',
            flaggedQuantityInfo: p.quantityInfo,
          });
          // Inconclusive = nothing was audited; unchanged = the data was never fixed, so a clean AI
          // verdict on it proves nothing. Leave both open rather than closing them blind.
          if (res.stillFlagged || res.inconclusive || res.unchanged) stillFlagged += group.length;
          else resolved.push(...group);
        } catch (err) {
          console.error('Failed to recheck quantity flag', p.productName, err);
          failed += group.length;
        }
        setProgress((n) => n + 1);
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(QTY_RECHECK_ALL_CONCURRENCY, queue.length) }, worker));
      if (resolved.length > 0) {
        await resolveAICheckerQuantityFlagsBulk(resolved.map((p) => ({
          traceId: p.restaurantId,
          articleId: p.articleId,
          productName: p.productName,
          source: p.source ?? 'pakbon',
        })));
        onResolved(resolved.map(aiQtyFlagKey));
      }
      setSummary({ resolved: resolved.length, stillFlagged, failed });
    } catch (err) {
      // The rechecks ran but persisting the resolutions failed — report them as failed, not as done.
      console.error('Failed to bulk-mark rechecked quantity flags done', err);
      setSummary({ resolved: 0, stillFlagged, failed: failed + resolved.length });
    }
    setBusy(false);
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        onClick={handle}
        disabled={busy}
        title={t('adminTesting.review.recheckAllTitle', { count: products.length, platform })}
        className={cn(secondaryText, 'flex items-center gap-1 text-[10px] font-sans font-normal underline disabled:opacity-50 hover:text-indigo-600 dark:hover:text-indigo-400')}
      >
        <RotateCw className={cn('w-3 h-3', busy && 'animate-spin')} />
        {busy
          ? t('adminTesting.review.recheckAllProgress', { done: progress, total })
          : t('adminTesting.review.recheckAll', { count: products.length })}
      </button>
      {summary && !busy && (
        <span
          className={cn(
            'text-[10px] px-1.5 py-0.5 rounded font-sans font-normal',
            summary.resolved > 0
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
          )}
        >
          {t('adminTesting.review.recheckAllResult', { resolved: summary.resolved, flagged: summary.stillFlagged })}
          {summary.failed > 0 ? ` · ${t('adminTesting.review.recheckAllFailed', { count: summary.failed })}` : ''}
        </span>
      )}
    </span>
  );
}

// Sends ALL of one supplier's open quantity flags to the code-monkey as ONE bulk session using the
// orchestrator flow ('quantity-issue-bulk', launched on the fable model) — distinct from the
// hand-picked multi-select batch, which stays on the normal 'quantity-issue' flow (opus).
export function QtyFlagSendAllMonkeyButton({ platform, products, t }: { platform: string; products: FlaggedProductWithRestaurant[]; t: TFn }) {
  const [state, setState] = useState<MonkeyState>('idle');
  const handle = async () => {
    if (state === 'loading') return;
    if (!window.confirm(t('adminTesting.review.sendAllToMonkeyConfirm', { count: products.length, platform }))) return;
    setState('loading');
    try {
      const caseInfo = `ALL ${products.length} open quantity-info flags for supplier '${platform}' are handed off together. Orchestrate per the base prompt: group them into root-cause patterns, delegate investigation and fixes to subagents, and deliver ONE consolidated SQL script with expected row counts. Issues are grouped by the source file their order came from, and each group ends with that file's raw extracted text — give every subagent the file text for its issues so the correct quantityInfo and the parsing fix are derived from what the parser actually saw, not the flag description alone.\n\n`
        + await buildGroupedQuantityCaseInfo(products);
      await sendIssueToCodeMonkey({
        flow: 'quantity-issue-bulk',
        caseInfo,
        context: {
          bulk: true,
          platform,
          count: products.length,
          issues: products.map((p) => ({
            restaurantName: p.restaurantName,
            articleId: p.articleId,
            productName: p.productName,
            source: p.source ?? 'pakbon',
          })),
        },
      });
      setState('done');
      setTimeout(() => setState('idle'), 2000);
    } catch (err) {
      console.error('Failed to send supplier bulk quantity flags to code-monkey', err);
      setState('error');
      setTimeout(() => setState('idle'), 4000);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={state === 'loading'}
      title={t('adminTesting.review.sendAllToMonkeyTitle', { count: products.length, platform })}
      className={cn(
        'flex items-center gap-1 text-[10px] font-sans font-normal underline disabled:opacity-50',
        state === 'error' ? 'text-red-500'
        : state === 'done' ? 'text-green-600 dark:text-green-400'
        : cn(secondaryText, 'hover:text-teal-600 dark:hover:text-teal-400'),
      )}
    >
      <MonkeyIcon className={cn('w-3 h-3', state === 'loading' && 'animate-spin')} />
      {state === 'done' ? t('adminTesting.review.menuCodeMonkeySent') : t('adminTesting.review.sendAllToMonkey', { count: products.length })}
    </button>
  );
}

// Sends the flags hand-picked INSIDE one supplier group to the code-monkey — same 'quantity-issue' batch
// as the card-header button, but scoped to this supplier so you don't have to scroll back up.
export function QtyFlagSendSelectedButton({ products, onSend, t }: { products: FlaggedProductWithRestaurant[]; onSend: (products: FlaggedProductWithRestaurant[]) => Promise<void>; t: TFn }) {
  const [state, setState] = useState<MonkeyState>('idle');
  const handle = async () => {
    if (state === 'loading') return;
    setState('loading');
    try {
      await onSend(products);
      setState('done');
      setTimeout(() => setState('idle'), 2000);
    } catch (err) {
      console.error('Failed to send selected quantity flags to code-monkey', err);
      setState('error');
      setTimeout(() => setState('idle'), 4000);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={state === 'loading'}
      title={t('adminTesting.review.sendSelectedToTerminal', { count: products.length })}
      className={cn(
        'flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-sans font-normal text-white dark:text-white transition disabled:opacity-60',
        state === 'error' ? 'bg-red-600 dark:bg-red-600' : state === 'done' ? 'bg-green-600 dark:bg-green-600' : 'bg-backgroundDarker-dark dark:bg-backgroundDarker-dark hover:opacity-85',
      )}
    >
      <MonkeyIcon className={cn('w-3 h-3', state === 'loading' && 'animate-spin')} />
      {state === 'done' ? t('adminTesting.review.menuCodeMonkeySent') : t('adminTesting.review.sendSelectedToTerminal', { count: products.length })}
    </button>
  );
}
